import { CapGainDistribution } from '@products/models/capgain-distribution';
import { CapGainDistributionDTO } from '@products/types/dto/capgain-distribution.dto.type';
import { CapGainListingFundStatus } from '@products/types/tax.type';
import { SiteConfigService } from '@services/site-config.service';
import {
  AssetClass,
  ConfigurationCountry,
  ConfigurationLocale,
  FundId,
  FundShareClassId,
  ProductType,
  ShareClassCode,
  WebProductTaxonomy,
} from '@types';
import { MapperFactory } from './mapper-factory';
import { Mapper, MapperParams } from './type.mapper';

export class CapGainDistributionsMapper extends Mapper<CapGainDistribution[]> {
  constructor(mapperParams: MapperParams, mapperFactory: MapperFactory) {
    super(mapperParams, mapperFactory);
  }

  toDomain(
    capGainDistributionsDTO: CapGainDistributionDTO[]
  ): CapGainDistribution[] {
    const capGainDistributions: CapGainDistribution[] = [];

    capGainDistributionsDTO.forEach(
      (capGainDistributionDTO: CapGainDistributionDTO) => {
        const capGainDistribution: CapGainDistribution = this.createMapper(
          CapGainDistributionMapper
        ).toDomain(capGainDistributionDTO);

        if (capGainDistribution) {
          capGainDistributions.push(capGainDistribution);
        }
      }
    );

    return capGainDistributions;
  }
}

export class CapGainDistributionMapper extends Mapper<CapGainDistribution> {
  constructor(mapperParams: MapperParams, mapperFactory: MapperFactory) {
    super(mapperParams, mapperFactory);
  }

  toDomain(
    capGainDistributionDTO: CapGainDistributionDTO
  ): CapGainDistribution {
    const fundId = capGainDistributionDTO.fundid as FundId;
    const shareClassCode = capGainDistributionDTO.shclcode as ShareClassCode;
    const shareClassKey = (shareClassCode
      ? `${fundId}-${shareClassCode}`
      : fundId) as FundShareClassId;

    return {
      fundId,
      shareClassCode,
      countryCode: capGainDistributionDTO.cntrycode as ConfigurationCountry,
      languageCode: capGainDistributionDTO.langcode as ConfigurationLocale,
      capitalGainPayableDate: capGainDistributionDTO.capgainpayabledate,
      capitalGainRecordDate: capGainDistributionDTO.capgainrecorddatefmt,
      capitalGainExDividendDate: capGainDistributionDTO.capgainexdivdatefmt,
      capitalGainReInvestmentDate:
        capGainDistributionDTO.capgainreinvestmentdate,
      shortTermCapitalGainAmount: capGainDistributionDTO.capgainamtshrtterm,
      longTermCapitalGainAmount: capGainDistributionDTO.capgainamtlngterm,
      totalDistributionAmountMonthly: capGainDistributionDTO.totaldistamtmth,
      capitalGainReInvestmentAmount:
        capGainDistributionDTO.capgainreinvstmntamt,
      estimateFlag: capGainDistributionDTO.estimateflag,
      fundName: capGainDistributionDTO.fundname,
      shareClassName: capGainDistributionDTO.shareclassname,
      cusip: capGainDistributionDTO.cusip,
      ticker: capGainDistributionDTO.ticker,
      capGainFrequency: capGainDistributionDTO.capgainfrequency,
      productType: capGainDistributionDTO.producttype as ProductType,
      taxYear: capGainDistributionDTO.taxyear,
      productTaxonomy: capGainDistributionDTO.webprdcttaxonomy as WebProductTaxonomy,
      fundStatus: this.getFundStatus(shareClassKey),
      investmentGroup: capGainDistributionDTO.investmentgroup,
      taId: capGainDistributionDTO.taid,
      primaryShareClassIndicator:
        capGainDistributionDTO.prmryshclind?.toLowerCase() === 'yes',
      capitalGainRecordDateFormatted:
        capGainDistributionDTO.capgainrecorddatefmt,
      capitalGainPayableDateFormatted:
        capGainDistributionDTO.capgainpayabledatefmt,
      longTermCapitalGainPercent: capGainDistributionDTO.ltcapgainpctnav,
      totalGainNav: capGainDistributionDTO.totcapgainpctnav,
      shortTermCapitalGainPercent: capGainDistributionDTO.stcapgainpctnav,
      navAsofDate: capGainDistributionDTO.navasofdate,
      navValue: capGainDistributionDTO.navvalue,
      assetClass: capGainDistributionDTO.assetclass as AssetClass,
    };
  }

  /**
   * Get fund status by fetching details from bloomreach configuration.
   * @param fundShareClassId combination of ONETIS FundId and ONETIS ShareClassCode
   */
  private getFundStatus(
    fundShareClassId: FundShareClassId
  ): keyof typeof CapGainListingFundStatus {
    if (this.mapperParams?.config?.isActiveShareClass(fundShareClassId)) {
      return CapGainListingFundStatus.active;
    } else if (
      this.mapperParams?.config?.isSoftLaunchShareClass(fundShareClassId)
    ) {
      return CapGainListingFundStatus['soft-launch'];
    } else if (
      this.mapperParams?.config?.isSoftClosedShareClass(fundShareClassId)
    ) {
      return CapGainListingFundStatus['soft-closed'];
    }
    return CapGainListingFundStatus.inactive;
  }
}
